// ═══════════════════════════════════════════════════════════════════════════
// List Detection Utilities — shared between run7 (body) and run25 (tables)
// ═══════════════════════════════════════════════════════════════════════════

import { getConfig } from '../taskpane/configService';

// FIX S4323: named alias for the repeated union type used across list-classification functions.
export type ListItemType = "bullet" | "number" | "alpha";

// ── Bullet character sets ──

// Solid bullets → Level 0  →  Body: "List Bullet"  |  Table: "Table:Bullet 1"
export const SOLID_BULLETS = [
  '\u2022', // • BULLET
  '\u25A0', // ■ BLACK SQUARE
  '\u25AA', // ▪ BLACK SMALL SQUARE
  '\u25C6', // ◆ BLACK DIAMOND
  '\u25CF', // ● BLACK CIRCLE
  '\u27A2', // ➢
  '\u27A3', // ➣
  '\u2B9A', // ⮚
  '\u00B7', // · MIDDLE DOT
  '\u2219', // ∙ BULLET OPERATOR
  '\uF0B7', // Symbol font bullet
  '\u25B6', // ▶
  '\u25BA', // ►
  '\u2666', // ♦
  '\u2713', // ✓
  '\u2714', // ✔
  '\u27A4', // ➤
  '\u2023', // ‣
  '\u2043', // ⁃
];

// Hollow bullets → Level 1  →  Body: "List Bullet 2"  |  Table: "Table:Bullet 2"
export const HOLLOW_BULLETS = [
  '\u25CB', // ○ WHITE CIRCLE
  '\u25AB', // ▫ WHITE SMALL SQUARE
  '\u25A1', // □ WHITE SQUARE
  '\u25C7', // ◇ WHITE DIAMOND
  '\u25C0', // ◀
  '\u25C4', // ◄
];

export const ALL_BULLET_CHARS = [...SOLID_BULLETS, ...HOLLOW_BULLETS];

// Private Use Area chars — invisible markers Word uses internally for list symbols
export const PUA_CHARS = new Set([
  '\uF0B7', '\uF0A7', '\uF076', '\uF0D8', '\uF0FC', '\uF0E8',
  '\uF0A8', '\uF071', '\uF06C', '\uF06E'
]);

// ── Styles to exclude ──
const EXCLUDE_STYLES = [
  'toc', 'heading', 'caption', 'title', 'subtitle',
  'footer', 'header', 'index', 'footnote', 'endnote',
  'lof', 'lot', 'loa', 'appendix'
];

export function shouldExclude(style: string): boolean {
  if (!style) return false;
  const s = style.toLowerCase().trim();
  return EXCLUDE_STYLES.some(ex => s.includes(ex));
}

// ── Heading detection ──
export function isNumberedHeading(text: string): boolean {
  if (!text) return false;
  if (!text.trim()) return false;
  const t = text.trim();
  if (/^\d+\.\d+/.test(t)) return true;
  if (/^\d+\.\s+[A-Z]/.test(t)) return true;
  return false;
}

export function isBodyBulletStyle(s: string): boolean {
  // Matches body bullet styles (level 1 or level 2) from config
  if (!s) return false;
  const { bulletStyles } = getConfig().run7_bodyBullets;
  const t = s.trim().toLowerCase();
  return t === bulletStyles.level1.toLowerCase() || t === bulletStyles.level2.toLowerCase();
}

export function isTableBulletStyle(s: string): boolean {
  // Matches table bullet styles (level 1 or level 2) from config.
  // Must contain ':' — real table list styles are named "Table:Bullet 1" etc.
  // Bare "Table" is a caption style and must never match here.
  if (!s) return false;
  const { bulletStyles } = getConfig().run25_tableListBullets;
  const t = s.trim().toLowerCase();
  return (
    (t === bulletStyles.level1.toLowerCase() || t === bulletStyles.level2.toLowerCase())
    && t.includes(':')
  );
}

export function isBodyNumberStyle(s: string): boolean {
  // Matches body numbered list style (level 1 only — no variants in the PDF)
  if (!s) return false;
  const { numberStyles } = getConfig().run7_bodyBullets;
  return s.trim().toLowerCase() === numberStyles.level1.toLowerCase();
}

export function isTableNumberStyle(s: string): boolean {
  // Matches table numbered list style (level 1 only — no variants in the PDF).
  // Must contain ':' — real table list styles are named "Table:List Number" etc.
  // Bare "Table" is a caption style and must never match here.
  if (!s) return false;
  const { numberStyle } = getConfig().run25_tableListBullets;
  const t = s.trim().toLowerCase();
  return (
    t === numberStyle.toLowerCase()
    && t.includes(':')
  );
}

export function isBodyAlphaStyle(s: string): boolean {
  // Matches body alpha list style (level 1 only — no variants in the PDF)
  if (!s) return false;
  const { alphaStyles } = getConfig().run7_bodyBullets;
  return s.trim().toLowerCase() === alphaStyles.level1.toLowerCase();
}

export function isTableAlphaStyle(s: string): boolean {
  // Matches table alpha list style (level 1 only — no variants in the PDF).
  // Must contain ':' — real table list styles are named "Table:List Alpha" etc.
  // Bare "Table" is a caption style and must never match here.
  if (!s) return false;
  const { alphaStyle } = getConfig().run25_tableListBullets;
  const t = s.trim().toLowerCase();
  return (
    t === alphaStyle.toLowerCase()
    && t.includes(':')
  );
}

export function isAnyBodyListStyle(s: string): boolean {
  return isBodyBulletStyle(s) || isBodyNumberStyle(s) || isBodyAlphaStyle(s);
}

export function isAnyTableListStyle(s: string): boolean {
  return isTableBulletStyle(s) || isTableNumberStyle(s) || isTableAlphaStyle(s);
}

// ── Level extraction from style name ──
export function getLevelFromBodyStyle(style: string): number {
  if (!style) return 0;
  const b = getConfig().run7_bodyBullets;
  const t = style.trim().toLowerCase();
  if (t === b.bulletStyles.level2.toLowerCase()) return 1;
  if (t === b.numberStyles.level2.toLowerCase()) return 1;
  if (t === b.alphaStyles.level2.toLowerCase()) return 1;
  return 0;
}

// ── Pattern detectors ──

export function detectBullet(text: string): boolean {
  if (!text) return false;
  if (!text.trim()) return false;
  const trimmed = text.trim();
  if (ALL_BULLET_CHARS.includes(trimmed[0])) return true;
  for (let i = 0; i < Math.min(5, text.length); i++) {
    if (ALL_BULLET_CHARS.includes(text[i])) return true;
  }
  return false;
}

export function detectNumberedList(text: string): boolean {
  if (!text) return false;
  if (!text.trim()) return false;
  const t = text.trim();
  if (/^\d+[.)]\s+\S/.test(t)) return true;
  if (/^[ivx]+[.)]\s+\S/i.test(t)) return true;
  return false;
}

export function detectAlphaList(text: string): boolean {
  if (!text) return false;
  if (!text.trim()) return false;
  const t = text.trim();
  if (/^[a-zA-Z][.)]\s+\S/.test(t)) return true;
  return false;
}

// ── Bullet level from bullet character (solid=0, hollow=1) ──
export function getBulletLevel(text: string): number {
  if (!text) return 0;
  for (let i = 0; i < Math.min(5, text.length); i++) {
    if (HOLLOW_BULLETS.includes(text[i])) return 1;
    if (SOLID_BULLETS.includes(text[i])) return 0;
  }
  const trimmed = text.trim();
  if (trimmed.length > 0) {
    if (HOLLOW_BULLETS.includes(trimmed[0])) return 1;
    if (SOLID_BULLETS.includes(trimmed[0])) return 0;
  }
  return 0;
}

export async function getIndentLevel(
  para: Word.Paragraph,
  context: Word.RequestContext
): Promise<number> {
  try {
    para.load("leftIndent");
    await context.sync();
    const indent = para.leftIndent || 0;
    if (indent >= 18) return 1;
    return 0;
  } catch {
    return 0;
  }
}

// ── Numbering-suppression check (OOXML-based) ──
export async function isNumberingSuppressed(
  para: Word.Paragraph,
  context: Word.RequestContext
): Promise<boolean> {
  try {
    const ooxmlResult = para.getOoxml();
    await context.sync();
    const xml = ooxmlResult.value || "";
    if (!xml) return false;

    let searchXml = xml;
    const bodyMatch = xml.match(/<w:body\b[^>]*>([\s\S]*?)<\/w:body>/);
    if (bodyMatch) searchXml = bodyMatch[1];

    const pBlocks: string[] = [];
    const pRegex = /<w:p\b[^>]*>[\s\S]*?<\/w:p>/g;
    let pm: RegExpExecArray | null;
    while ((pm = pRegex.exec(searchXml)) !== null) pBlocks.push(pm[0]);
    if (pBlocks.length === 0) return false;
    const pXml = pBlocks[pBlocks.length - 1];

    // FIX S6594: use RegExp.exec() instead of String.match()
    const numPrMatch = /<w:numPr\b[^>]*\/>|<w:numPr\b[^>]*>[\s\S]*?<\/w:numPr>/.exec(pXml);
    if (!numPrMatch) return false;

    // FIX S6594: use RegExp.exec() instead of String.match()
    const numIdMatch = /<w:numId\s+w:val="(\d+)"/.exec(numPrMatch[0]);
    if (!numIdMatch) return false;
    if (numIdMatch[1] === "0") return true;

    return false;
  } catch (e) {
    console.warn("isNumberingSuppressed: error reading OOXML:", e);
    return false;
  }
}

// ── Header text heuristic ──

const TITLE_CASE_SMALL_WORDS = new Set([
  'a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'from', 'if', 'in',
  'into', 'of', 'on', 'or', 'per', 'the', 'to', 'via', 'vs', 'with',
  'within', 'without', "n't"
]);

export function isTitleCase(text: string): boolean {
  if (!text) return false;
  const stripped = text.trim().replace(/[:;,.!?\s]+$/, '');
  const words = stripped.split(/\s+/).filter(Boolean);
  if (words.length === 0) return false;

  let contentWords = 0;
  let capitalizedContent = 0;
  for (const w of words) {
    if (TITLE_CASE_SMALL_WORDS.has(w.toLowerCase())) continue;
    contentWords++;
    if (w[0] && /[A-Z]/.test(w[0])) capitalizedContent++;
  }
  if (contentWords === 0) return false;
  return capitalizedContent === contentWords;
}

export function startsWithListMarker(text: string): boolean {
  if (!text) return false;
  const t = text.trim();
  if (!t) return false;
  if (ALL_BULLET_CHARS.includes(t[0])) return true;
  if (/^\d+[.)]\s/.test(t)) return true;
  if (/^\(\d+\)\s/.test(t)) return true;
  if (/^[a-z][.)]\s/i.test(t)) return true;
  if (/^\([a-z]+\)\s/i.test(t)) return true;
  if (/^[ivxlcdm]+[.)]\s/i.test(t)) return true;
  return false;
}

export function looksLikeHeader(text: string): boolean {
  if (!text) return false;
  const trimmed = text.trim();
  if (!trimmed.endsWith(':')) return false;
  if (trimmed.length > 80) return false;
  if (startsWithListMarker(trimmed)) return false;
  return isTitleCase(trimmed);
}

// ── Demotion: removing a paragraph from a Word list ──

export async function detachParagraphFromList(
  para: Word.Paragraph,
  context: Word.RequestContext
): Promise<boolean> {
  let apiThrew = false;
  try {
    para.detachFromList();
    await context.sync();
  } catch (e) {
    apiThrew = true;
    console.warn("  detachFromList API threw:", (e as Error).message);
  }

  if (!apiThrew) {
    try {
      const lon = para.listOrNullObject;
      lon.load("isNullObject");
      await context.sync();
      if (lon.isNullObject) return true;
      console.warn("  detachFromList claimed success but paragraph still in list — trying OOXML fallback");
    } catch (e) {
      console.warn("  Post-detach verification failed:", (e as Error).message);
    }
  }

  try {
    const ooxmlResult = para.getOoxml();
    await context.sync();
    const xml = ooxmlResult.value || "";
    if (!xml) return false;

    const bodyMatch = xml.match(/<w:body\b[^>]*>([\s\S]*?)<\/w:body>/);
    if (!bodyMatch) {
      console.warn("  OOXML fallback: no <w:body> element found");
      return false;
    }

    const body = bodyMatch[1];
    const pMatch = body.match(/<w:p\b[^>]*>[\s\S]*?<\/w:p>/);
    if (!pMatch) {
      console.warn("  OOXML fallback: no <w:p> in body");
      return false;
    }

    const oldP = pMatch[0];
    const newP = oldP.replace(
      /<w:numPr\b[^>]*\/>|<w:numPr\b[^>]*>[\s\S]*?<\/w:numPr>/g,
      ''
    );
    if (oldP === newP) return false;

    const newXml = xml.replace(oldP, newP);
    para.insertOoxml(newXml, Word.InsertLocation.replace);
    await context.sync();
    return true;
  } catch (e) {
    console.warn("  OOXML detach failed:", (e as Error).message);
    return false;
  }
}

export async function demoteFromList(
  para: Word.Paragraph,
  context: Word.RequestContext,
  fallbackStyle: string = "Normal"
): Promise<boolean> {
  let detached = false;
  try {
    detached = await detachParagraphFromList(para, context);
  } catch (e) {
    console.warn("  demote: detach phase threw:", (e as Error).message);
  }

  try {
    para.style = fallbackStyle;
    await context.sync();
    return true;
  } catch {
    if (fallbackStyle !== "Normal") {
      try {
        para.style = "Normal";
        await context.sync();
        return true;
      } catch (error_) {
        // FIX S7718: renamed e2 → error_ per SonarLint naming convention
        console.warn("  demote: could not set Normal either:", (error_ as Error).message);
      }
    }
  }
  return detached;
}

export async function hasAnyListAffiliation(
  para: Word.Paragraph,
  context: Word.RequestContext,
  paraStyle: string
): Promise<boolean> {
  if (isAnyBodyListStyle(paraStyle) || isAnyTableListStyle(paraStyle)) return true;
  try {
    const lon = para.listOrNullObject;
    lon.load("isNullObject");
    await context.sync();
    return !lon.isNullObject;
  } catch {
    return false;
  }
}

// ── Visible marker check for listString ──
export function hasVisibleMarker(listString: string): boolean {
  if (!listString) return false;
  if (!listString.trim()) return false;

  for (const ch of listString.trim()) {
    if (ch === ' ' || ch === '\t') continue;
    if (ALL_BULLET_CHARS.includes(ch)) return true;
    if (PUA_CHARS.has(ch)) continue;
    return true;
  }
  return false;
}

// FIX S3776: extracted from classifyListItem to reduce its cognitive complexity.
// Classifies a marker string as alpha, number, or null based purely on its pattern.
function classifyByMarkerString(ls: string): "number" | "alpha" | null {
  // alpha marker:  "a."  "a)"  "(a)"
  if (/^[a-zA-Z][.)]/.test(ls) || /^\([a-z]\)/i.test(ls)) return "alpha";
  // numeric marker:  "1."  "1)"  "(1)"
  if (/^\d+[.)]/.test(ls) || /^\(\d+\)/.test(ls)) return "number";
  // roman numeral marker:  "i."  "iv)"  "(iv)"
  if (/^[ivx]+[.)]/.test(ls) || /^\([ivx]+\)/i.test(ls)) return "number";
  return null;
}

export function classifyListItem(
  levelType: string,
  listString: string
): ListItemType | null {
  if (!hasVisibleMarker(listString)) return null;
  const ls = listString.trim();

  // Check 1 — Word itself says it's a bullet level type
  if (levelType === "Bullet" || levelType === "Picture") return "bullet";

  // Check 2 — the marker string contains a known bullet character
  if (ALL_BULLET_CHARS.some(bc => ls.includes(bc))) return "bullet";

  // Checks 3–5 — sequential list markers (alpha / numeric / roman).
  // ✅ THE FIX — returns null for unrecognised text like "Table 1", "Figure 2",
  // "Appendix A" etc. that have visible text but match no real list-marker pattern.
  // null = "not a recognised list marker" → caller skips this paragraph.
  return classifyByMarkerString(ls);
}

// FIX S3776: extracted from getTargetStyle to reduce its cognitive complexity.
function getTableTargetStyle(listType: ListItemType, level: number): string {
  const t = getConfig().run25_tableListBullets;
  if (listType === "bullet") {
    return level <= 0 ? t.bulletStyles.level1 : t.bulletStyles.level2;
  } else if (listType === "alpha") {
    return t.alphaStyle;
  } else {
    return t.numberStyle;
  }
}

// FIX S3776: extracted from getTargetStyle to reduce its cognitive complexity.
function getBodyTargetStyle(listType: ListItemType, level: number): string {
  const b = getConfig().run7_bodyBullets;
  if (listType === "bullet") {
    return level <= 0 ? b.bulletStyles.level1 : b.bulletStyles.level2;
  } else if (listType === "alpha") {
    return b.alphaStyles.level1;
  } else {
    return b.numberStyles.level1;
  }
}

export function getTargetStyle(
  listType: ListItemType,
  level: number,
  inTable: boolean
): string {
  if (inTable) return getTableTargetStyle(listType, level);
  return getBodyTargetStyle(listType, level);
}

export async function ensureListStyleExists(
  context: Word.RequestContext,
  targetStyle: string,
  verifiedCache: Set<string>
): Promise<boolean> {

  if (verifiedCache.has(targetStyle)) return false;

  const styles = context.document.getStyles();
  const styleCheck = styles.getByNameOrNullObject(targetStyle);
  styleCheck.load("isNullObject");
  await context.sync();

  if (!styleCheck.isNullObject) {
    verifiedCache.add(targetStyle);
    return false;
  }

  console.log(`  Style "${targetStyle}" not found — creating...`);

  const cfg = getConfig();
  const b = cfg.run7_bodyBullets;
  const t = cfg.run25_tableListBullets;
  const ts = targetStyle.trim().toLowerCase();

  let baseName = "Normal";
  if (ts === t.bulletStyles.level1.toLowerCase())       baseName = b.bulletStyles.level1;
  else if (ts === t.bulletStyles.level2.toLowerCase())  baseName = b.bulletStyles.level2;
  else if (ts === b.bulletStyles.level2.toLowerCase())  baseName = b.bulletStyles.level1;
  else if (ts === b.bulletStyles.level1.toLowerCase())  baseName = b.bulletStyles.level1;
  else if (ts === b.numberStyles.level1.toLowerCase())  baseName = b.numberStyles.level1;
  else if (ts === b.alphaStyles.level1.toLowerCase())   baseName = b.alphaStyles.level1;
  else if (ts === t.numberStyle.toLowerCase())          baseName = b.numberStyles.level1;
  else if (ts === t.alphaStyle.toLowerCase())           baseName = b.alphaStyles.level1;

  const baseCheck = styles.getByNameOrNullObject(baseName);
  baseCheck.load("isNullObject");
  await context.sync();
  if (baseCheck.isNullObject) {
    console.log(`  Base style "${baseName}" not found — falling back to "Normal"`);
    baseName = "Normal";
  }

  const newStyle = context.document.addStyle(targetStyle, Word.StyleType.paragraph);
  await context.sync();

  try {
    newStyle.baseStyle = baseName;
    await context.sync();
    console.log(`  ✓ Style "${targetStyle}" created (based on "${baseName}")`);
  } catch (e) {
    console.log(`  ✓ Style "${targetStyle}" created (could not set base: ${(e as Error).message})`);
  }

  verifiedCache.add(targetStyle);
  return true;
}

// ═══════════════════════════════════════════════════════════════════════════
// Cross-run "is this a list item?" predicate
// ═══════════════════════════════════════════════════════════════════════════

export interface ListLikeResult {
  isList: boolean;
  reason: string;
}

// FIX S3776: extracted from isListLikeParagraph to reduce its cognitive complexity.
// Probes Word's own list/listItem API and returns a ListLikeResult if the paragraph
// is part of a Word list with a visible marker; returns null if it is not (or on error).
async function probeWordListItem(
  para: Word.Paragraph,
  context: Word.RequestContext
): Promise<ListLikeResult | null> {
  try {
    const listOrNull = para.listOrNullObject;
    listOrNull.load("isNullObject");
    await context.sync();
    if (listOrNull.isNullObject) return null;

    const listItem = para.listItem;
    listItem.load("listString");
    await context.sync();
    const ls = (listItem.listString || "").trim();
    if (hasVisibleMarker(ls)) {
      return { isList: true, reason: `Word list item with marker "${ls}"` };
    }
    return null;
  } catch (e) {
    console.warn(`  isListLikeParagraph: list probe threw — ${(e as Error).message}`);
    return null;
  }
}

export async function isListLikeParagraph(
  para: Word.Paragraph,
  context: Word.RequestContext,
  paraText: string,
  paraStyle: string
): Promise<ListLikeResult> {
  // Rule 2: explicit list/bullet style by name — both contexts.
  if (isAnyBodyListStyle(paraStyle))  return { isList: true, reason: `body list style "${paraStyle}"` };
  if (isAnyTableListStyle(paraStyle)) return { isList: true, reason: `table list style "${paraStyle}"` };

  // Rule 3: Word's built-in "List Paragraph" wrapper style.
  if (/^list\s*paragraph$/i.test(paraStyle.trim())) {
    return { isList: true, reason: `built-in "List Paragraph" style` };
  }

  // Rule 1: Word considers it part of a list (with a visible marker).
  const wordListResult = await probeWordListItem(para, context);
  if (wordListResult) return wordListResult;

  // Rule 4: text starts with a list-marker pattern.
  if (detectBullet(paraText))        return { isList: true, reason: "text starts with bullet glyph" };
  if (detectNumberedList(paraText))  return { isList: true, reason: "text starts with numbered marker" };
  if (detectAlphaList(paraText))     return { isList: true, reason: "text starts with alpha marker" };

  return { isList: false, reason: "" };
}